import type {
  NestedFilter
} from '@@partner:types:graphql'
import type { DeliverySlot } from '@@partner:types:app'

import React, { useCallback, useMemo, useState } from 'react'

import { FlexboxGrid, Pagination, Panel, SelectPicker, Tag, Checkbox, Nav, Button, Modal } from 'rsuite'
import { useQuery } from '@apollo/client'
import { CalendarOutline, ListOutline } from 'react-ionicons'

import LogisticTopMenu from '@components/logistic/LogisticTopMenu'
import FilterPanel from '@components/common/filter/FilterPanel'
import { FilterConfig } from '@components/common/filter/FilterConfig'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'
import SlotBulkUpdatePanel from '@components/logistic/slots/SlotBulkUpdatePanel'
import DeliverySlotsCalendar from '@components/logistic/slots/DeliverySlotsCalendar'
import ShopSlotForm from '@components/shop/slot/ShopSlotForm'

import { GQL_QUERY_ALL_SLOTS, FetchAllSlots, FetchAllSlotsVariables } from '@@partner:graphql/query/shop/allSlots.gql'
import { useFetchAllDeliveryZones } from '@@partner:graphql/query/deliveryZone/allDeliveryZones.gql'
import { SORT_ORDER_ASC, SORT_ORDER_DESC } from '@@partner:config/graphql'
import { currentBrandVar } from '@@partner:graphql/initial-data'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { formatDate, formatDayMonthYear } from '@@partner:helpers/util'
import { makeBetweenFilter } from '@@partner:helpers/crud/filters'
import { GQL_ID } from '@@partner:types:graphql'

import './DeliverySlotsPage.scss'

const DEFAULT_LIMIT_OPTIONS = [ 10, 20, 50, 100 ]

type ViewMode = 'calendar' | 'table'

// ===== Типы для фильтра =====
interface SlotsFilterState {
  zone_id: string
  ts_start: { from?: number; to?: number }
}

const defaultFilterState: SlotsFilterState = {
  zone_id: '',
  ts_start: { from: Date.now(), to: undefined }
}

// ===== Хелпер для цвета заполненности =====
const getColor = (percent: number) => {
  if (percent >= 90) return 'red'
  if (percent >= 70) return 'orange'
  return 'green'
}

export default function DeliverySlotsPage () {
  const currentBrand = currentBrandVar()
  const currentBrandId = currentBrand?.id

  const [ viewMode, setViewMode ] = useState<ViewMode>('calendar')

  // Инициализируем apiFilter сразу с фильтром по умолчанию (ts_start >= сейчас)
  const [ apiFilter, setApiFilter ] = useState<NestedFilter | null>(() => ({
    fields: [ 'ts_start' ],
    op: 'gte',
    value: [ defaultFilterState.ts_start.from! ]
  }))
  const [ selectedSlots, setSelectedSlots ] = useState<GQL_ID[]>([])
  const [ showBulkUpdate, setShowBulkUpdate ] = useState(false)
  const [ editSlotId, setEditSlotId ] = useState<GQL_ID | null>(null)

  // Загрузка зон доставки для фильтра
  const { data: zonesData, loading: loadingZones } = useFetchAllDeliveryZones(
    currentBrandId ? {
      filter: {
        fields: [ 'brand_id' ],
        op: 'eq',
        value: currentBrandId
      }
    } : undefined
  )

  const deliveryZones = useMemo(() => zonesData?.allDeliveryZones?.edges ?? [], [ zonesData ])

  const zoneOptions = useMemo(() => [
    { label: 'Все зоны', value: '' },
    ...deliveryZones.map(zone => ({
      label: `${zone.name} (${zone.shop?.name ?? 'Без магазина'})`,
      value: zone.id
    }))
  ], [ deliveryZones ])

  // Конфигурация фильтров
  const filterConfig: FilterConfig[] = useMemo(() => [
    {
      key: 'zone_id',
      type: 'select',
      placeholder: 'Выберите зону доставки',
      data: zoneOptions,
      searchable: true,
      size: 'wide'
    },
    {
      key: 'ts_start',
      type: 'date-range',
      placeholder: 'Время начала слота'
    }
  ] as FilterConfig[], [ zoneOptions ])

  // Базовый фильтр (всегда только зональные слоты)
  const baseFilter: NestedFilter = useMemo(() => ({
    fields: [ 'shop_id' ],
    op: 'is_null'
  }), [])

  // Комбинированный фильтр
  const combinedFilter: NestedFilter | undefined = useMemo(() => {
    if (!apiFilter) return baseFilter
    return { and: [ baseFilter, apiFilter ] }
  }, [ baseFilter, apiFilter ])

  const { page, limit, defaultLimitOptions, tableSort, handlePageSelect, handleLimitSelect, handleSortColumn } =
    useGridHelper({
      defaultSortFieldName: 'ts_start',
      defaultSortDirection: SORT_ORDER_ASC,
      defaultSortType: 'asc',
      defaultLimit: 20,
      defaultLimitOptions: DEFAULT_LIMIT_OPTIONS
    })

  // Используем useQuery напрямую с cache-first чтобы избежать бесконечного цикла
  const { data: slotsData, loading, error, refetch } = useQuery<FetchAllSlots, FetchAllSlotsVariables>(
    GQL_QUERY_ALL_SLOTS,
    {
      variables: {
        filter: combinedFilter,
        pager: { page, limit },
        sort: { fieldName: tableSort.fieldName, direction: tableSort.direction }
      },
      fetchPolicy: 'cache-first'
    }
  )

  const slots = useMemo(() => slotsData?.allSlots?.edges ?? [], [ slotsData ])
  const pageInfo = slotsData?.allSlots?.pageInfo

  // Проверяем наличие фильтров для массового обновления
  const hasFilters = useMemo(() => !!apiFilter, [ apiFilter ])

  // Преобразование фильтра для API
  const transformFilter = useCallback((state: SlotsFilterState): NestedFilter | null => {
    const filters: NestedFilter[] = []
    
    if (state.zone_id) {
      filters.push({
        fields: [ 'zone_id' ],
        op: 'eq',
        value: [ state.zone_id ]
      })
    }
    
    // Поддерживаем фильтр "от" без "до" и наоборот
    if (state.ts_start?.from || state.ts_start?.to) {
      const periodFilter = makeBetweenFilter('ts_start', {
        from: state.ts_start.from,
        to: state.ts_start.to
      })
      filters.push(periodFilter)
    }
    
    if (filters.length === 0) return null
    if (filters.length === 1) return filters[0]
    return { and: filters }
  }, [])

  const handleFilterSearch = useCallback((state: SlotsFilterState) => {
    const filter = transformFilter(state)
    setApiFilter(filter)
    handlePageSelect(1)
  }, [ transformFilter, handlePageSelect ])

  const handleSelectSlot = useCallback((slotId: GQL_ID) => {
    setSelectedSlots(prev => 
      prev.includes(slotId) 
        ? prev.filter(id => id !== slotId)
        : [ ...prev, slotId ]
    )
  }, [])

  const handleBulkUpdateSuccess = useCallback(() => {
    setSelectedSlots([])
    refetch()
  }, [ refetch ])

  // Колонки для ResponsiveTable
  const columns: ResponsiveTableColumn<DeliverySlot>[] = useMemo(() => [
    {
      dataKey: 'select',
      header: '',
      width: 50,
      render: (slot) => (
        <Checkbox
          checked={selectedSlots.includes(slot.id)}
          onChange={() => handleSelectSlot(slot.id)}
        />
      ),
      hideOnMobile: true
    },
    {
      dataKey: 'id',
      header: 'ID',
      sortable: true,
      flexGrow: 1,
      isCardTitle: true
    },
    {
      dataKey: 'zone',
      header: 'Магазин / Зона',
      flexGrow: 2,
      render: (slot) => (
        <div>
          {slot.shop ? (
            <>
              <div>{slot.shop.name}</div>
              <small style={{ color: '#888' }}>{slot.shop.address ?? ''}</small>
            </>
          ) : slot.deliveryZone ? (
            <>
              <div>{slot.deliveryZone.name}</div>
              <small style={{ color: '#888' }}>Зона доставки</small>
            </>
          ) : (
            <div>—</div>
          )}
        </div>
      ),
      renderMobile: (slot) => slot.deliveryZone?.name ?? slot.shop?.name ?? '—'
    },
    {
      dataKey: 'ts_start',
      header: 'Дата',
      sortable: true,
      flexGrow: 1,
      isCardBadge: true,
      render: (slot) => formatDayMonthYear(new Date(parseInt(slot.period.from)))
    },
    {
      dataKey: 'slotTime',
      header: 'Время',
      flexGrow: 2
    },
    {
      dataKey: 'timeout',
      header: 'Доступен до',
      sortable: true,
      flexGrow: 2,
      render: (slot) => (
        <div>
          <div>{formatDate(new Date(parseInt(slot.availableUntil)))}</div>
          <small style={{ color: '#888' }}>Таймаут: {Math.round(slot.timeout / 60000)} мин</small>
        </div>
      ),
      hideOnMobile: true
    },
    {
      dataKey: 'limits',
      header: 'Лимиты',
      flexGrow: 2,
      render: (slot) => (
        <div>
          <div>{slot.orders_capacity} заказов</div>
          <div>{slot.position_capacity} позиций</div>
        </div>
      ),
      hideOnMobile: true
    },
    {
      dataKey: 'fill',
      header: 'Заполненность',
      flexGrow: 2,
      render: (slot) => {
        const ordersPercent = slot.orders_capacity > 0 
          ? Math.round((slot.orders_count / slot.orders_capacity) * 100) 
          : 0
        const positionsPercent = slot.position_capacity > 0 
          ? Math.round((slot.positions_count / slot.position_capacity) * 100) 
          : 0

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <Tag size="sm" color={getColor(ordersPercent)}>
              {slot.orders_count}/{slot.orders_capacity} ({ordersPercent}%)
            </Tag>
            <Tag size="sm" color={getColor(positionsPercent)}>
              {slot.positions_count}/{slot.position_capacity} ({positionsPercent}%)
            </Tag>
          </div>
        )
      },
      renderMobile: (slot) => {
        const ordersPercent = slot.orders_capacity > 0 
          ? Math.round((slot.orders_count / slot.orders_capacity) * 100) 
          : 0
        return (
          <Tag size="sm" color={getColor(ordersPercent)}>
            {ordersPercent}%
          </Tag>
        )
      }
    },
    {
      dataKey: 'actions',
      header: '',
      width: 100,
      render: (slot) => (
        <Button
          appearance="link"
          size="xs"
          onClick={() => setEditSlotId(slot.id)}
        >
          Изменить
        </Button>
      ),
      hideOnMobile: true
    }
  ], [ selectedSlots, handleSelectSlot ])

  return (
    <div className="DeliverySlotsPage">
      <h1 className="u-heading">Слоты доставки</h1>
      <LogisticTopMenu />

      {/* Переключатель видов */}
      <Nav 
        appearance="subtle" 
        activeKey={viewMode} 
        onSelect={(key) => setViewMode(key as ViewMode)}
        style={{ marginBottom: 16 }}
      >
        <Nav.Item 
          eventKey="calendar" 
          icon={<CalendarOutline width="16px" height="16px" />}
        >
          Календарь
        </Nav.Item>
        <Nav.Item 
          eventKey="table" 
          icon={<ListOutline width="16px" height="16px" />}
        >
          Таблица
        </Nav.Item>
      </Nav>

      {viewMode === 'calendar' ? (
        <DeliverySlotsCalendar />
      ) : (
        <Panel shaded className="mt-4">
          <FilterPanel<SlotsFilterState, SlotsFilterState>
            filters={filterConfig}
            defaultState={defaultFilterState}
            onSearch={handleFilterSearch}
            showBulkUpdate={showBulkUpdate}
            onBulkUpdate={setShowBulkUpdate}
            syncWithUrl={true}
            searchOnInit={true}
          />

          <SlotBulkUpdatePanel
            show={selectedSlots.length > 0 || showBulkUpdate}
            selectedIds={selectedSlots}
            filter={hasFilters && showBulkUpdate ? combinedFilter : undefined}
            onSuccess={handleBulkUpdateSuccess}
          />

          <ResponsiveTable<DeliverySlot>
            data={slots}
            columns={columns}
            loading={loading}
            rowKey="id"
            sortColumn={tableSort.fieldName}
            sortType={tableSort.direction === SORT_ORDER_DESC ? 'desc' : 'asc'}
            onSortColumn={handleSortColumn}
            emptyMessage="Нет слотов"
          />

          <FlexboxGrid justify="space-between" align="middle" style={{ marginTop: 10 }}>
            <Pagination
              last
              first
              next
              prev
              ellipsis
              total={pageInfo?.total ?? 0}
              activePage={page}
              pages={pageInfo?.lastPage}
              maxButtons={10}
              onSelect={handlePageSelect}
            />
            <SelectPicker
              value={limit}
              data={defaultLimitOptions.map((key: number) => ({ value: key, label: key }))}
              cleanable={false}
              searchable={false}
              onChange={handleLimitSelect}
            />
          </FlexboxGrid>
        </Panel>
      )}

      {/* Модалка редактирования слота */}
      <Modal open={!!editSlotId} onClose={() => setEditSlotId(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Редактирование слота</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editSlotId && (
            <ShopSlotForm
              slotId={editSlotId}
              onSuccess={() => {
                setEditSlotId(null)
                refetch()
              }}
              onCancel={() => setEditSlotId(null)}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}
